'use client';
import { useDerivedState } from '@hooks/useDerivedState';
import { classNames } from '@repo/ui/src/utils/classNames';
import React, { useCallback } from 'react';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import { HandleChange } from '@components/Cart/_SingleLineItem';

interface Props {
  handleChange: HandleChange;
  quantity: number;
  max?: number;
  min?: number;
  className?: string;
}

const Stepper = ({ quantity, handleChange, max = 100, min = 0, className }: Props) => {
  const [internalQuantity, setInternalQuantity] = useDerivedState(quantity);
  const disableMax = internalQuantity === max;
  const disableMin = internalQuantity === min;
  const { t } = useTranslation();

  const handleIncrement = useCallback(() => {
    setInternalQuantity((currValue: number) => {
      const newQuantity = currValue < max ? currValue + 1 : max;
      handleChange(newQuantity);
      return newQuantity;
    });
  }, [setInternalQuantity, handleChange, max]);

  const handleDecrement = useCallback(() => {
    setInternalQuantity((currValue: number) => {
      const newQuantity = currValue ? currValue - 1 : min;
      handleChange(newQuantity);
      return newQuantity;
    });
  }, [setInternalQuantity, handleChange, min]);

  const handleInput = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setInternalQuantity((currValue: number) => {
        const val = parseInt(event.target.value, 10);
        const newQuantity = Number.isNaN(val) ? currValue : val;
        if (newQuantity <= max && newQuantity >= min) {
          handleChange(newQuantity);
          return newQuantity;
        }
        if (newQuantity > max) {
          handleChange(max);
          return max;
        }
        handleChange(min);
        return min;
      });
    },
    [setInternalQuantity, handleChange, max, min],
  );

  return (
    <div className={classNames('tw-flex', className)}>
      <button
        className="disabled:tw-cursor-not-allowed tw-w-6 tw-h-6 tw-border tw-border-neutral-200 tw-border-solid"
        onClick={handleDecrement}
        aria-label={t('cart.lineItemText.removeText', undefined, { default: 'decrease' })}
        data-testid="c-decrement-btn"
        disabled={disableMin}
      >
        <p
          className={classNames(
            disableMin && 'tw-text-neutral-200',
            'tw-mb-0 tra-text-web-body-m tw-h-full',
          )}
        >
          -
        </p>
      </button>
      <input
        className="hideArrows [appearance:textfield] tw-w-8 tw-h-6 tw-border-t tw-border-b tw-border-neutral-200 tra-text-web-body-m tw-bg-white tw-block tw-text-center tw-rounded-none"
        value={internalQuantity}
        aria-label={`Quantity ${internalQuantity}`}
        onChange={handleInput}
        type="number"
        max={max}
        min={min}
      />

      <button
        className="disabled:tw-cursor-not-allowed tw-w-6 tw-h-6 tw-border tw-border-neutral-200 tw-border-solid"
        onClick={handleIncrement}
        aria-label={t('cart.lineItemText.addText', undefined, { default: 'increase' })}
        data-testid="c-increment-btn"
        disabled={disableMax}
      >
        <p
          className={classNames(
            disableMax && 'tw-text-neutral-200',
            'tw-mb-0 tra-text-web-body-m tw-h-full',
          )}
        >
          +
        </p>
      </button>
    </div>
  );
};

export default Stepper;
